import React, { useEffect, useState } from "react";
import { loginUser, recoverPasswordUser, registerUser } from "../utils/auth";
import logoImage from "../Logo/GB SWIM FANTASY LOGO.png";

const REMEMBERED_USERNAME_KEY = "fantasySwimRememberedUsername";

const Login = ({ onAuthSuccess, initialError = "" }) => {
  const [mode, setMode] = useState("login"); // login | register | recover
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [coachName, setCoachName] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showIntro, setShowIntro] = useState(true);
  const [error, setError] = useState(initialError);
  const [successMessage, setSuccessMessage] = useState("");

  const isRegisterMode = mode === "register";
  const isRecoverMode = mode === "recover";

  useEffect(() => {
    setError(initialError);
  }, [initialError]);

  useEffect(() => {
    const rememberedUsername = localStorage.getItem(REMEMBERED_USERNAME_KEY);
    if (rememberedUsername) {
      setUsername(rememberedUsername);
      setRememberMe(true);
    }
  }, []);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    )?.matches;

    if (prefersReducedMotion) {
      setShowIntro(false);
      return;
    }

    const timer = window.setTimeout(() => {
      setShowIntro(false);
    }, 3000);

    return () => {
      window.clearTimeout(timer);
    };
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setSuccessMessage("");

    if (!username.trim() || !password) {
      setError("Please enter both username and password.");
      return;
    }

    if ((isRegisterMode || isRecoverMode) && !coachName.trim()) {
      setError("Please enter your coach name.");
      return;
    }

    const result = isRegisterMode
      ? await registerUser({ username, password, coachName })
      : isRecoverMode
        ? await recoverPasswordUser({
            username,
            coachName,
            newPassword: password,
          })
        : await loginUser({ username, password });

    if (!result.ok) {
      setError(result.error);
      return;
    }

    if (isRecoverMode) {
      setError("");
      setSuccessMessage(result.message || "Password updated.");
      setMode("login");
      setPassword("");
      return;
    }

    if (rememberMe) {
      localStorage.setItem(REMEMBERED_USERNAME_KEY, username.trim());
    } else {
      localStorage.removeItem(REMEMBERED_USERNAME_KEY);
    }

    setError("");
    setSuccessMessage("");
    onAuthSuccess(result.user);
    setPassword("");
  };

  const switchMode = (nextMode) => {
    setMode(nextMode);
    setError("");
    setSuccessMessage("");
    setPassword("");
  };

  const handleEnterSubmit = (e) => {
    if (e.key === "Enter") {
      handleLogin(e);
    }
  };

  return (
    <div className="login-outer">
      <div
        className={
          showIntro
            ? "login-intro-overlay"
            : "login-intro-overlay login-intro-overlay-hidden"
        }
        aria-hidden={!showIntro}
      >
        <div className="login-intro-logo-wrap">
          <img
            src={logoImage}
            alt="GB Swim Fantasy"
            className="login-intro-logo"
          />
        </div>
      </div>

      <div
        className={showIntro ? "login-card login-card-hidden" : "login-card"}
      >
        <h1 className="login-title">
          {isRegisterMode
            ? "Create Coach Account"
            : isRecoverMode
              ? "Recover Password"
              : "Coach Login"}
        </h1>
        <form onSubmit={handleLogin} className="login-form">
          <input
            type="text"
            placeholder="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            onKeyDown={handleEnterSubmit}
            className="login-input"
          />
          {(isRegisterMode || isRecoverMode) && (
            <input
              type="text"
              placeholder="Coach Name"
              value={coachName}
              onChange={(e) => setCoachName(e.target.value)}
              onKeyDown={handleEnterSubmit}
              className="login-input"
            />
          )}
          <input
            type="password"
            placeholder={isRecoverMode ? "New Password" : "Password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={handleEnterSubmit}
            className="login-input"
          />
          {!isRegisterMode && !isRecoverMode && (
            <label className="login-remember-row">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setRememberMe(checked);
                  if (!checked) {
                    localStorage.removeItem(REMEMBERED_USERNAME_KEY);
                  }
                }}
                className="login-remember-checkbox"
              />
              <span>Remember me</span>
            </label>
          )}
          <button type="submit" className="login-button">
            {isRegisterMode
              ? "Create Account"
              : isRecoverMode
                ? "Reset Password"
                : "Login"}
          </button>
        </form>

        <div className="login-action-row">
          <button
            type="button"
            onClick={() => switchMode("login")}
            className="login-secondary-button"
          >
            Login
          </button>
          <button
            type="button"
            onClick={() => switchMode("register")}
            className="login-secondary-button"
          >
            Sign up
          </button>
          <button
            type="button"
            onClick={() => switchMode("recover")}
            className="login-secondary-button"
          >
            Forgot password
          </button>
        </div>
        {error && <p className="login-error">{error}</p>}
        {successMessage && <p className="login-success">{successMessage}</p>}
      </div>
    </div>
  );
};

export default Login;
